import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { getRevisionPaymentLink } from '../../config/paymentLinks';

const RevisionPaymentModal = () => {
  const {
    showRevisionPaymentModal,
    setShowRevisionPaymentModal,
    revisionProject,
    revisionData,
    setProjects,
    showNotification,
    typeNames,
    documentTypes
  } = useAppContext();

  const [processing, setProcessing] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!showRevisionPaymentModal) {
      setAcceptedTerms(false);
      setProcessing(false);
    }
  }, [showRevisionPaymentModal]);

  if (!showRevisionPaymentModal || !revisionProject || !revisionData) return null;

  // SAFETY CHECK: Stage 1 projects should never have revision payments
  if (revisionProject?.stage === 1) {
    console.error('Stage 1 projects should not have revision payments');
    setShowRevisionPaymentModal(false);
    showNotification('Error', 'Revisions are not available for Stage 1 projects', 'error');
    return null;
  }

  const handlePayment = () => {
    if (!acceptedTerms) {
      showNotification("Terms Required", "Please accept the terms and conditions to proceed with payment", "error");
      return;
    }

    setProcessing(true);
    
    // FOR STAGE 2 REVISIONS - REDIRECT TO STRIPE
    if (revisionProject.stage === 2 && revisionData.fee > 0) {
      console.log('=== REVISION PAYMENT DEBUG ===');
      console.log('Project:', revisionProject.id);
      console.log('Revision fee:', revisionData.fee);
      console.log('Revision number:', revisionData.revisionNumber);
      
      // Save revision payment info to localStorage
      localStorage.setItem('pendingRevisionPaymentProjectId', String(revisionProject.id));
      localStorage.setItem('pendingRevisionPaymentType', 'additional-revision');
      localStorage.setItem('pendingRevisionPaymentStage', '2');
      localStorage.setItem('pendingRevisionPaymentFee', String(revisionData.fee));
      localStorage.setItem('pendingRevisionPaymentDocType', revisionData.documentType || '');
      localStorage.setItem('pendingRevisionPaymentFeedback', revisionData.feedback || '');
      localStorage.setItem('pendingRevisionPaymentNumber', String(revisionData.revisionNumber || 1));
      // Save uploaded file URLs (files were uploaded before reaching this modal)
      localStorage.setItem('pendingRevisionPaymentFiles', JSON.stringify(revisionData.supportingFiles || []));
      
      // Convert fee to number to ensure proper lookup
      const feeAsNumber = typeof revisionData.fee === 'string' ? parseInt(revisionData.fee) : revisionData.fee;
      
      // Get the correct Stripe URL from paymentLinks.js - INCLUDE PROJECT ID
      const stripeUrl = getRevisionPaymentLink(2, feeAsNumber, revisionProject.id);
      
      // Enhanced debugging
      console.log('=== URL DEBUG ===');
      console.log('Original fee type:', typeof revisionData.fee);
      console.log('Original fee value:', revisionData.fee);
      console.log('Converted fee:', feeAsNumber);
      console.log('URL returned:', stripeUrl);
      console.log('Expected URL for $150:', 'https://buy.stripe.com/4gMfZadJobAcajDakk5wI1i');
      console.log('URLs match?', stripeUrl === 'https://buy.stripe.com/4gMfZadJobAcajDakk5wI1i');
      
      if (!stripeUrl) {
        console.error('No revision payment link found for fee:', feeAsNumber);
        showNotification('Error', 'Payment link not configured. Please contact support.', 'error');
        setProcessing(false);
        return;
      }
      
      console.log('Final Stripe URL to redirect:', stripeUrl);
      console.log('Saved to localStorage:', {
        projectId: localStorage.getItem('pendingRevisionPaymentProjectId'),
        fee: localStorage.getItem('pendingRevisionPaymentFee'),
        revisionNumber: localStorage.getItem('pendingRevisionPaymentNumber')
      });
      
      // Show redirecting message
      showNotification("Redirecting to Stripe", "You'll be redirected to secure payment for revision...", "info");
      
      // Ensure localStorage is written before redirect
      localStorage.setItem('lastPaymentRedirect', new Date().toISOString());
      
      // Redirect to Stripe after ensuring data is saved
      setTimeout(() => {
        console.log('Redirecting to Stripe now...');
        window.location.href = stripeUrl;
      }, 1500); // Increased delay to ensure localStorage writes complete
      
      return;
    }

    // FOR STAGE 3/4 OR FREE REVISIONS - SIMULATE PAYMENT
    setTimeout(() => {
      // Update project with payment confirmation AND submit the revision request
      setProjects(prev => prev.map(p => {
        if (p.id === revisionProject.id) {
          // Create payment key based on whether it's Stage 3/4 with document type
          let paymentKey;
          let updatedTracking = { ...p.documentRevisionTracking };
          
          if (revisionProject.stage >= 3 && revisionData.documentType) {
            // Stage 3/4 with document type tracking
            paymentKey = `${revisionData.documentType}_revision${revisionData.revisionNumber}PaymentStatus`;
            
            // Update document revision tracking
            if (!updatedTracking[revisionData.documentType]) {
              updatedTracking[revisionData.documentType] = {
                revisionsUsed: 0,
                maxIncluded: 1,
                documents: []
              };
            }
            updatedTracking[revisionData.documentType].revisionsUsed = revisionData.revisionNumber;
          } else {
            // Stage 2
            paymentKey = `revision${revisionData.revisionNumber}PaymentStatus`;
          }
          
          const nextRevisionNumber = revisionData.revisionNumber;
          
          // CRITICAL FIX: Properly update Stage 2 revision tracking
          let updatedRevisions = p.revisions;
          if (revisionProject.stage === 2) {
            updatedRevisions = p.revisions ? {
              ...p.revisions,
              currentRound: Math.max(nextRevisionNumber, p.revisions.currentRound || 0),
              totalRevisionsUsed: Math.max((p.revisions.totalRevisionsUsed || 0) + 1, nextRevisionNumber)
            } : {
              currentRound: nextRevisionNumber,
              maxIncludedRevisions: 1,
              totalRevisionsUsed: nextRevisionNumber,
              additionalRevisionFee: revisionData.fee
            };
          }
          
          return {
            ...p,
            [paymentKey]: 'paid',
            status: `revision-${nextRevisionNumber}-requested`,
            documentRevisionTracking: revisionProject.stage >= 3 ? updatedTracking : p.documentRevisionTracking,
            revisions: updatedRevisions,
            communications: [
              ...p.communications,
              // Revision request submission FIRST (client action)
              {
                type: 'client',
                sender: revisionProject.client,
                message: `Revision ${nextRevisionNumber} requested${revisionData.documentTypeLabel ? ` for ${revisionData.documentTypeLabel}` : ''}: ${revisionData.feedback}${revisionData.supportingFiles && revisionData.supportingFiles.length > 0 ? ` (${revisionData.supportingFiles.length} supporting file(s) attached)` : ''}`,
                timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
              },
              // Payment system message SECOND
              {
                type: 'system',
                sender: 'planAIdesign System',
                message: `Payment of $${revisionData.fee} received for Revision ${revisionData.revisionNumber}${revisionData.documentTypeLabel ? ` - ${revisionData.documentTypeLabel}` : ''}.`,
                timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
              },
              // Payment confirmation THIRD
              {
                type: 'architect',
                sender: 'planAIdesign',
                message: 'Thank you for your payment. Work on your project has started.',
                timestamp: new Date(Date.now() + 2000).toISOString().slice(0, 16).replace('T', ' ')
              },
              // Auto-acknowledgment LAST
              {
                type: "architect",
                sender: "planAIdesign",
                message: `We have received your revision request${revisionData.documentTypeLabel ? ` for ${revisionData.documentTypeLabel}` : ''} (payment confirmed). We will begin working on it and provide an update shortly.`,
                timestamp: new Date(Date.now() + 3000).toISOString().slice(0, 16).replace('T', ' ')
              }
            ],
            revisionDocuments: [
              ...(p.revisionDocuments || []),
              {
                documentType: revisionData.documentType || null,
                revisionNumber: nextRevisionNumber,
                requirements: revisionData.feedback,
                files: revisionData.supportingFiles || [],
                uploadDate: new Date().toISOString()
              }
            ]
          };
        }
        return p;
      }));
      
      showNotification(
        "Payment & Revision Submitted", 
        `Payment processed successfully. Your revision request${revisionData.documentTypeLabel ? ` for ${revisionData.documentTypeLabel}` : ''} has been submitted to planAIdesign.`, 
        "success"
      );
      
      setProcessing(false);
      setShowRevisionPaymentModal(false);
      
      // Do NOT reopen the revision request modal - the revision is already submitted
    }, 2000);
  };

  const getButtonText = () => {
    if (processing) return 'Processing...';
    if (revisionProject.stage === 2 && revisionData.fee > 0) {
      return `Pay $${revisionData.fee} via Stripe`;
    }
    return `Pay $${revisionData.fee}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-8 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold">
            Additional Revision Payment
          </h3>
          <button 
            onClick={() => setShowRevisionPaymentModal(false)} 
            className="text-2xl text-slate-400 hover:text-slate-600"
          >✕</button>
        </div>

        <div className="mb-6">
          <div className="bg-purple-50 p-4 rounded-lg mb-4">
            <h4 className="font-semibold text-lg mb-2">{revisionProject.name}</h4>
            <p className="text-sm text-gray-600">
              {typeNames[revisionProject.type]} | Stage {revisionProject.stage}
            </p>
            <div className="mt-3 pt-3 border-t border-purple-200">
              <p className="text-sm font-semibold text-gray-600 mb-1">
                Revision {revisionData.revisionNumber} (Additional)
                {revisionData.documentTypeLabel && (
                  <span className="block text-xs mt-1">
                    Document Type: {revisionData.documentTypeLabel}
                  </span>
                )}
              </p>
              <p className="text-2xl font-bold text-purple-600">
                ${revisionData.fee}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Additional revision fee - Your project includes 1 revision{revisionData.documentTypeLabel ? ` for ${revisionData.documentTypeLabel}` : ''}
              </p>
            </div>
          </div>

          {/* FOR STAGE 2 - Show Stripe redirect message */}
          {revisionProject.stage === 2 && revisionData.fee > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">Secure Payment via Stripe</p>
                  <p>You'll be redirected to Stripe's secure payment page to complete this revision payment.</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-yellow-50 p-4 rounded-lg mb-4 border border-yellow-200">
            <h5 className="font-semibold text-sm mb-2 text-yellow-800">Why is there a fee?</h5>
            <p className="text-xs text-gray-700">
              {revisionProject.stage >= 3 && revisionData.documentType ? (
                <>Each document type includes 1 revision round at no additional cost. 
                This is revision {revisionData.revisionNumber} for {revisionData.documentTypeLabel}, 
                which requires the additional revision fee as outlined in your original agreement.</>
              ) : (
                <>Your project includes {revisionProject.revisions?.maxIncludedRevisions || 1} revision round(s) at no additional cost. 
                This is revision {revisionData.revisionNumber}, which requires the additional revision fee as outlined in your original agreement.</>
              )}
            </p>
          </div>

          {revisionData.feedback && (
            <div className="bg-blue-50 p-4 rounded-lg mb-4 border border-blue-200">
              <h5 className="font-semibold text-sm mb-2 text-blue-800">Your Revision Requirements</h5>
              <p className="text-xs text-gray-700 whitespace-pre-wrap">{revisionData.feedback}</p>
              {revisionData.supportingFiles && revisionData.supportingFiles.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs font-semibold text-gray-600">Supporting files:</p>
                  <ul className="text-xs text-gray-600 mt-1">
                    {revisionData.supportingFiles.map((file, idx) => (
                      <li key={idx}>• {file.name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Terms and Conditions */}
          <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h5 className="font-semibold text-sm mb-2">Revision Payment Terms</h5>
            <div className="text-xs text-gray-700 space-y-2 mb-3">
              <p>• Payment must be completed before revision work can begin.</p>
              <p>• Once payment is processed, your revision request will be automatically submitted.</p>
              <p>• This fee covers one complete revision round based on your feedback{revisionData.documentTypeLabel ? ` for ${revisionData.documentTypeLabel}` : ''}.</p>
              <p>• Further revisions {revisionData.documentTypeLabel ? 'for this document type ' : ''}will incur additional fees.</p>
              <p>• No refunds once revision work has commenced.</p>
            </div>
            
            <label className="flex items-start gap-2 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-xs text-gray-700">
                I understand this is an additional revision fee and accept the terms.
              </span>
            </label>
          </div>
        </div>

        <div className="flex gap-4 mt-6">
          <button 
            onClick={() => setShowRevisionPaymentModal(false)}
            className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600"
          >
            Cancel
          </button>
          <button 
            onClick={handlePayment}
            disabled={processing || !acceptedTerms}
            className="flex-1 bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:bg-gray-400"
          >
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RevisionPaymentModal;